/**
 * Article rédigé le 2026-09-06 à partir de sources web ouvertes et vérifiées automatiquement
 * (6 URL testées, révisé après vérification). Daté au moment des faits : 2025-02-13.
 * À relire avant/après mise en ligne. Format : voir src/blog/README.md
 */
export default {
  "slug": "sommet-action-ia-paris-2025-vu-de-besancon",
  "title": "Sommet pour l’action sur l’IA : ce que Paris a décidé, vu de Besançon",
  "excerpt": "Une semaine d’événements, deux jours au Grand Palais, plus d’une centaine d’engagements et 109 milliards d’euros annoncés. Ce que ce sommet parisien change, ou non, pour le bassin bisontin.",
  "seoTitle": "Sommet pour l’action sur l’IA vu de Besançon",
  "seoDescription": "Une semaine d’événements à Paris, plus de cent engagements et 109 milliards d’euros annoncés : ce que le sommet de février 2025 change pour le bassin bisontin.",
  "category": "ia",
  "tags": [
    "Sommet pour l'Action sur l'IA",
    "French Tech BFC",
    "FEMTO-ST",
    "Printemps de l'IA",
    "Hors Besançon"
  ],
  "author": "jimmy",
  "publishedAt": "2025-02-13",
  "updatedAt": "2025-02-13",
  "featured": false,
  "cover": {
    "image": "/img/blog/artificial-intelligence-action-summit-at-the-grand-palais-pa.jpg",
    "credit": "Photo : Le Grand Palais pendant le Sommet pour l'action sur l'IA, Paris, février 2025 — Dati Bendo / European Union, 2025 / EC - Audiovisual Service, CC BY 4.0, via Wikimedia Commons",
    "alt": "La façade du Grand Palais à Paris, avec ses colonnes et ses statues, habillée d'un totem et de bannières « Sommet pour l'action sur l'IA / AI Action Summit, 10 et 11 février 2025 »."
  },
  "content": [
    {
      "type": "lead",
      "text": "Du 6 au 11 février, Paris a été la capitale mondiale de l’intelligence artificielle : journées scientifiques, week-end culturel, puis deux jours de sommet international sous la verrière du Grand Palais. Il en ressort plus d’une centaine d’engagements et des montants d’investissement inédits pour la France. Reste la question qui nous occupe ici : qu’est-ce que cela déplace, concrètement, dans le bassin bisontin ?"
    },
    {
      "type": "keyfacts",
      "facts": [
        {
          "label": "Quoi",
          "value": "Sommet pour l’action sur l’IA : une semaine d’événements et un sommet international [^1]"
        },
        {
          "label": "Quand",
          "value": "Du 6 au 11 février 2025, sommet les 10 et 11 février [^1][^2]"
        },
        {
          "label": "Où",
          "value": "Grand Palais, Paris ; plus d’une centaine d’événements annoncés en marge du sommet, dont une journée entreprises à Station F [^3]"
        },
        {
          "label": "Qui",
          "value": "Coprésidence France-Inde [^3] ; près de 1 500 participants aux deux journées, acteurs venus de plus de cent pays [^1]"
        },
        {
          "label": "Pour qui",
          "value": "États, chercheurs, entreprises — et, localement, les PME industrielles en veille"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Une semaine entière, puis deux jours au Grand Palais"
    },
    {
      "type": "p",
      "text": "Depuis le 6 février, Paris enchaînait journées scientifiques, week-end culturel, comité interministériel sur l’IA et événements parallèles, dont une journée dédiée aux entreprises [^1][^2]. Le sommet proprement dit s’est tenu les 10 et 11 février au Grand Palais, sous coprésidence française et indienne, autour de cinq thèmes : IA au service de l’intérêt public, avenir du travail, innovation et culture, confiance dans l’IA, gouvernance mondiale [^3]."
    },
    {
      "type": "p",
      "text": "Près de 1 500 participants ont pris part à ces deux journées. Le 11 février, les acteurs venus de plus de cent pays ont annoncé **plus d’une centaine d’actions et d’engagements concrets**, autour de trois axes : l’appropriation de l’IA par tous, une IA durable, une gouvernance internationale plus efficace et plus inclusive [^1]."
    },
    {
      "type": "h2",
      "text": "Les montants annoncés, et ce qu’ils recouvrent"
    },
    {
      "type": "p",
      "text": "Le chiffre qui a dominé la semaine est celui annoncé le 9 février par le président de la République : **109 milliards d’euros** d’investissements privés dans l’IA en France [^1][^5]. Ce n’est pas une enveloppe publique, mais un agrégat d’engagements privés au détail inégalement documenté."
    },
    {
      "type": "ul",
      "items": [
        "Un campus et un centre de données financés par les Émirats arabes unis, entre 30 et 50 milliards d’euros, pour une localisation encore indéterminée [^1][^5]",
        "Vingt milliards d’euros annoncés par le fonds canadien Brookfield pour un centre de données à Cambrai [^5]",
        "Le premier centre de données français de *Mistral AI*, près de Paris [^5]",
        "Trente-cinq sites « prêts à l’emploi » identifiés pour accueillir des centres de données [^5]",
        "À l’échelle européenne, l’initiative « InvestAI » lancée le 11 février pour mobiliser 200 milliards d’euros, dont un fonds de 20 milliards dédié à des giga-usines d’IA [^1]"
      ]
    },
    {
      "type": "p",
      "text": "S’y ajoute *Current AI*, initiative d’intérêt général dotée de 400 millions de dollars, avec un objectif de 2,5 milliards sur cinq ans [^5]. Ces annonces prolongent la troisième étape de la stratégie nationale pour l’IA, lancée en février 2025 autour de quatre priorités : infrastructures de calcul et maillons critiques de la chaîne de valeur, talents, accélération des usages, IA de confiance [^1]."
    },
    {
      "type": "h2",
      "text": "L’appel de la DGE, relayé depuis la Bourgogne-Franche-Comté"
    },
    {
      "type": "p",
      "text": "En amont du sommet, la Direction générale des entreprises avait lancé, dans le cadre de son initiative « IA pour l’Efficience », un appel à manifestation d’intérêt international, relayé localement par [La French Tech Bourgogne-Franche-Comté](https://lafrenchtechbfc.fr/opportunite/sommet-pour-laction-sur-lia-2025/) [^4]. Trois domaines étaient visés : l’administration publique, les entreprises et l’industrie. Les organisations ayant déjà mené un projet d’IA améliorant la compétitivité, l’innovation ou la productivité pouvaient candidater — microentreprises, PME, ETI, administrations, organisations internationales — en anglais, jusqu’au 15 janvier 2025, les dossiers retenus devant être valorisés pendant le sommet : visibilité internationale et échanges avec des experts [^4]."
    },
    {
      "type": "p",
      "text": "À ce stade, aucune liste publique de projets retenus n’est disponible : impossible de dire si des laboratoires ou des entreprises du bassin bisontin figurent parmi les dossiers déposés."
    },
    {
      "type": "h2",
      "text": "Ce que cela peut vouloir dire ici (analyse)"
    },
    {
      "type": "p",
      "text": "Les annonces parisiennes portent d’abord sur des infrastructures lourdes : centres de données, capacité de calcul, énergie. Besançon ne joue pas sur ce terrain. Sa carte est ailleurs, du côté des usages et de la recherche appliquée — microtechniques, temps-fréquence, [santé](/blog/besancon-terrain-naturel-ia-sante-chu-isifc-temis), instrumentation —, avec un tissu de PME industrielles pour lesquelles l’IA relève moins de la puissance de calcul que des données propres, de la maintenance prédictive et du contrôle qualité. La priorité nationale « accélérer les usages » [^1] est celle qui parle le plus au territoire."
    },
    {
      "type": "p",
      "text": "Le prochain rendez-vous local permettra d’en juger : la deuxième édition du *[Printemps de l’IA](/blog/printemps-ia-2026-besancon-cci-industrie)* est annoncée pour ce printemps à Besançon, coorganisée par le Pôle des Microtechniques et [l’institut FEMTO-ST](/blog/femto-st-besancon-capitale-microtechniques). Elle vise les TPE et PME industrielles : conférences, tables rondes et cas d’usage, village d’offreurs de solutions, démonstrations en direct [^6]."
    },
    {
      "type": "callout",
      "tone": "tip",
      "title": "Pourquoi ça compte pour Besançon",
      "text": "Un sommet international ne transforme pas un atelier. Mais il fixe un cadre — financements, priorités, vocabulaire — dont les dispositifs régionaux se saisissent ensuite. Pour une PME du Doubs, la question utile n’est pas « combien de milliards ? » mais « quel guichet, quelle brique de données, quel partenaire de recherche ». Le rendez-vous bisontin du printemps en donnera une partie de la réponse."
    },
    {
      "type": "h2",
      "text": "Ce qu’il faudra vérifier dans les mois qui viennent"
    },
    {
      "type": "p",
      "text": "Trois points méritent d’être suivis : la localisation effective des centres de données annoncés ; la traduction, pour les entreprises régionales, des dispositifs nationaux et européens ; la publication, ou non, des projets retenus par la DGE. L’écart entre les chiffres annoncés à Paris et les projets réellement engagés en Bourgogne-Franche-Comté restera la mesure la plus honnête de ce sommet."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "Paris accueille le sommet pour l’action sur l’IA",
      "publisher": "info.gouv.fr — Gouvernement français",
      "url": "https://www.info.gouv.fr/actualite/paris-accueille-le-sommet-pour-laction-sur-lia",
      "type": "official",
      "date": "2025-02-10"
    },
    {
      "id": 2,
      "title": "Artificial Intelligence Action Summit",
      "publisher": "Présidence de la République — elysee.fr",
      "url": "https://www.elysee.fr/en/sommet-pour-l-action-sur-l-ia",
      "type": "official",
      "date": ""
    },
    {
      "id": 3,
      "title": "AI Action Summit",
      "publisher": "Ministère de l’Europe et des Affaires étrangères",
      "url": "https://www.diplomatie.gouv.fr/en/presse-et-ressources/decouvrir-et-informer/actualites/sommet-pour-l-action-sur-l-intelligence-artificielle",
      "type": "official",
      "date": ""
    },
    {
      "id": 4,
      "title": "Sommet pour l’action sur l’IA 2025 : appel à manifestation d’intérêt de la DGE",
      "publisher": "La French Tech Bourgogne-Franche-Comté",
      "url": "https://lafrenchtechbfc.fr/opportunite/sommet-pour-laction-sur-lia-2025/",
      "type": "official",
      "date": ""
    },
    {
      "id": 5,
      "title": "Intelligence artificielle : Emmanuel Macron annonce 109 milliards d’euros d’investissements en France",
      "publisher": "macommune.info",
      "url": "https://www.macommune.info/intelligence-artificielle-emmanuel-macron-annonce-109-milliards-deuros-dinvestissements-en-france/",
      "type": "press",
      "date": "2025-02-10"
    },
    {
      "id": 6,
      "title": "Printemps de l’IA 2025 : rendez-vous pour la 2e édition",
      "publisher": "Institut FEMTO-ST",
      "url": "https://www.femto-st.fr/en/node/5395",
      "type": "official",
      "date": ""
    }
  ]
};
